/**
 * OneTrust -> Zaraz consent bridge.
 *
 * Zaraz consent follows OneTrust's own state. The bridge never decides
 * consent by itself and never grants from a click it saw.
 *
 * Purpose IDs come from Zaraz -> Consent:
 *   jCtP = Analytics (OneTrust C0002)
 *   mGtt = Marketing (OneTrust C0004, C0005 and OSSTA_BG)
 *
 * Rules (7 Oct 2026):
 *  1. OneTrust not loaded yet: grant nothing. Only take away a purpose that
 *     OneTrust's stored cookie does not allow. A visitor with no stored
 *     choice keeps everything denied, also when an ad blocker stops OneTrust.
 *  2. OneTrust loaded, banner not closed: deny.
 *  3. Banner closed: the groups OneTrust reports decide.
 *  4. Exception to 3: outside a notice rule, a close with X (OneTrust
 *     interaction type 3) is not a choice, so deny. A notice rule is one where
 *     OneTrust itself accepts on page click or on the next page ("By continuing
 *     to use this website..."). Today that is only "US National Audience".
 *     The rule is read from OneTrust's domain data, never from geography.
 *
 * Zaraz page views: zaraz.consent.set() marks consent as changed even when the
 * values are the same, and a replay after it sends the page view again. So
 * the bridge calls set() only when a value changes, never denies a purpose it
 * must grant again on the same page, and replays held events only when a
 * purpose goes from denied to granted.
 */
export const ZARAZ_CONSENT_BRIDGE = `(function () {
  // The inline head copy and the client bundle both run this. One is enough.
  if (window.__rhConsentBridge) return;
  window.__rhConsentBridge = true;

  var ANALYTICS = "jCtP";
  var MARKETING = "mGtt";
  var MARKETING_GROUPS = ["C0004", "C0005", "OSSTA_BG"];
  // OneTrust writes intType to the OptanonConsent cookie: 1 Accept All,
  // 2 Reject All, 3 banner closed with X, 6 Confirm My Choices.
  var CLOSED_WITH_X = "3";

  // Google Consent Mode v2 default - deny everything.
  var DENIED_DEFAULT = {
    "ad_storage": "denied",
    "ad_user_data": "denied",
    "ad_personalization": "denied",
    "analytics_storage": "denied"
  };
  function setGoogleDefault() {
    if (window.zaraz && typeof zaraz.set === "function") {
      zaraz.set("google_consent_default", DENIED_DEFAULT);
    }
  }
  setGoogleDefault();

  // Flags from the earlier bridge, which granted on X and on page clicks.
  // Nothing reads them now.
  try {
    window.localStorage.removeItem("rh_consent_banner_x");
    window.localStorage.removeItem("rh_consent_interacted");
  } catch (e) {}

  function readCookie(name) {
    var parts = document.cookie.split("; ");
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(name + "=") === 0) return parts[i].slice(name.length + 1);
    }
    return null;
  }
  function bannerClosed() {
    return readCookie("OptanonAlertBoxClosed") !== null;
  }
  // OptanonConsent cookie: groups as {id: "1" | "0"} and the interaction type.
  function storedConsent() {
    var raw = readCookie("OptanonConsent");
    var out = { groups: null, intType: null };
    if (!raw) return out;
    var fields = raw.split("&");
    for (var i = 0; i < fields.length; i++) {
      var eq = fields[i].indexOf("=");
      if (eq === -1) continue;
      var key = fields[i].slice(0, eq);
      var value;
      try { value = decodeURIComponent(fields[i].slice(eq + 1)); } catch (e) { continue; }
      if (key === "intType") out.intType = value;
      if (key === "groups") {
        out.groups = {};
        var pairs = value.split(",");
        for (var j = 0; j < pairs.length; j++) {
          var kv = pairs[j].split(":");
          if (kv.length === 2) out.groups[kv[0]] = kv[1];
        }
      }
    }
    return out;
  }

  // Marketing needs C0004, and no other marketing group that OneTrust uses
  // may be switched off.
  // isOn(id) / isOff(id) let the same rule run on live and stored groups.
  function statusFrom(isOn, isOff) {
    var marketing = isOn("C0004");
    for (var i = 0; i < MARKETING_GROUPS.length; i++) {
      if (isOff(MARKETING_GROUPS[i])) marketing = false;
    }
    var status = {};
    status[ANALYTICS] = isOn("C0002");
    status[MARKETING] = marketing;
    return status;
  }
  function denied() {
    var status = {};
    status[ANALYTICS] = false;
    status[MARKETING] = false;
    return status;
  }

  function oneTrustLoaded() {
    return !!(window.OneTrust && typeof OneTrust.GetDomainData === "function" &&
      typeof window.OnetrustActiveGroups === "string");
  }
  function domainData() {
    try { return OneTrust.GetDomainData() || null; } catch (e) { return null; }
  }
  // A notice rule: OneTrust itself treats a page click or the next page as
  // acceptance. These flags are set on the US National Audience rule only.
  function noticeRule(data) {
    if (!data) return false;
    var model = data.ConsentModel && data.ConsentModel.Name;
    return model === "opt-out" &&
      (data.OnClickAcceptAllCookies === true || data.NextPageAcceptAllCookies === true);
  }
  // What OneTrust says now. Call only after OneTrust has loaded.
  function oneTrustStatus() {
    if (!bannerClosed()) return denied();
    var data = domainData();
    if (!noticeRule(data) && storedConsent().intType === CLOSED_WITH_X) return denied();
    var active = "," + window.OnetrustActiveGroups + ",";
    // OneTrust lists in its cookie only the groups it uses on this rule
    // (C0005 has no cookies, so it is not listed). ":0" there means off.
    var stored = storedConsent().groups || {};
    function isOn(id) { return active.indexOf("," + id + ",") !== -1; }
    function isOff(id) { return !isOn(id) && stored[id] === "0"; }
    return statusFrom(isOn, isOff);
  }

  // What OneTrust's stored cookie allows, before OneTrust has loaded.
  function storedStatus() {
    var stored = storedConsent();
    if (!bannerClosed() || !stored.groups) return denied();
    function isOn(id) { return stored.groups[id] === "1"; }
    function isOff(id) { return stored.groups[id] === "0"; }
    return statusFrom(isOn, isOff);
  }

  function zarazReady() {
    return !!(window.zaraz && zaraz.consent && zaraz.consent.APIReady);
  }
  function zarazStatus() {
    var status = denied();
    try {
      var current = zaraz.consent.getAll() || {};
      status[ANALYTICS] = !!current[ANALYTICS];
      status[MARKETING] = !!current[MARKETING];
    } catch (e) {}
    return status;
  }

  function setGoogleUpdate(status) {
    if (!(window.zaraz && typeof zaraz.set === "function")) return;
    var ads = status[MARKETING] ? "granted" : "denied";
    zaraz.set("google_consent_update", {
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
      analytics_storage: status[ANALYTICS] ? "granted" : "denied"
    });
  }

  function apply(status) {
    if (!zarazReady()) return;
    setGoogleUpdate(status);
    var current = zarazStatus();
    var changed = false;
    var newlyGranted = false;
    for (var purpose in status) {
      if (status[purpose] !== current[purpose]) changed = true;
      if (status[purpose] && !current[purpose]) newlyGranted = true;
    }
    if (!changed) return;
    zaraz.consent.set(status);
    // Replay the held page view only when a purpose was just granted on this
    // page. On a page that loaded with consent already granted, Zaraz sent
    // the page view at load, so a replay would count it twice.
    if (newlyGranted) {
      try { zaraz.consent.sendQueuedEvents(); } catch (e) {}
    }
  }

  function sync() {
    if (!zarazReady()) return;
    if (oneTrustLoaded()) {
      apply(oneTrustStatus());
      return;
    }
    // Before OneTrust loads: never grant, only take away what the stored
    // choice does not allow. A purpose that the stored choice allows stays as
    // Zaraz holds it, so a consented visitor is not denied and re-granted.
    var current = zarazStatus();
    var stored = storedStatus();
    var status = {};
    for (var purpose in current) status[purpose] = current[purpose] && stored[purpose];
    apply(status);
  }
  function syncSoon() {
    setTimeout(sync, 0);
    setTimeout(sync, 300);
    setTimeout(sync, 1000);
  }

  var prior = window.OptanonWrapper;
  window.OptanonWrapper = function () {
    if (typeof prior === "function") prior();
    sync();
  };
  window.addEventListener("OneTrustGroupsUpdated", syncSoon);
  // OneTrust handles banner buttons and, on a notice rule, page clicks. Read
  // its state again after any click. The click itself grants nothing.
  document.addEventListener("click", function (event) {
    if (event.isTrusted) syncSoon();
  }, true);
  document.addEventListener("zarazConsentAPIReady", function () {
    // The first head call can precede Zaraz. Reapply the default once its
    // API is ready so Google requests receive a complete gcd state.
    setGoogleDefault();
    sync();
  });
  sync();
})();`;
